import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { tableCellClasses, tableRowClasses } from '@mui/material';
import { TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { Table, TableCellText, TablePrimaryCell } from '../../../../components/Metrics/MetricTable';
import { Metric } from '../../../../types/Analytics/Metric';
import { setupMetricChange } from '../../../../components/Metrics/MetricCard';
import Column from '../../../../components/Column';
import MetricChangeLabel from '../../../../components/Metrics/MetricChangeLabel';
import HatUtils from '../../../../utils/HatUtils';
import Utils from '../../../../components/Utils';

import { QueryRegistryKey } from '../../../../services/trialfire/hooks/QueryRegistry';
import { useHatDashboardPageContext } from '../../../../hooks/Pages/HatDashboardPage';

interface TableRow {
    channel: string;
    totalUniqueTraffic: Metric;
    totalAdInfluencedTraffic: Metric;
    percentAdInfluenced: Metric;
    tooltip?: string;
}

interface TableColumn {
    label: string;
    totalKey: QueryRegistryKey;
    totalAdInfluencedKey: QueryRegistryKey;
}

interface TableState {
    rows: TableRow[];
    setRows: Dispatch<SetStateAction<TableRow[]>>;
}

interface TableProps {
    rows: TableRow[];
}

const columns: TableColumn[] = [
    {
        label: 'STV/OLV/Display',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_ICAN',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_ICAN',
    },
    {
        label: 'Direct',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_DIRECT',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_DIRECT',
    },
    {
        label: 'Paid Search',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_PAID_SEARCH',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_PAID_SEARCH',
    },
    {
        label: 'Social',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_SOCIAL',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_SOCIAL',
    },
    {
        label: 'Organic Search',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_ORGANIC_SEARCH',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_ORGANIC_SEARCH',
    },
    {
        label: 'Referral',
        totalKey: 'TOTAL_UNIQUE_TRAFFIC_REFERRAL',
        totalAdInfluencedKey: 'TOTAL_AD_INFLUENCED_TRAFFIC_REFERRAL',
    },
];

const useHatChannelTrafficTable = (): TableState => {
    const { metrics, queries, previousQueries } = useHatDashboardPageContext();
    const [rows, setRows] = useState<TableRow[]>([]);

    const buildMetric = useCallback(
        (key: QueryRegistryKey, previousKey: QueryRegistryKey, format: Metric['format'] = 'number'): Metric =>
            setupMetricChange({
                format,
                value: queries[key]?.data?.data ?? 0,
                previousValue: previousQueries[previousKey]?.data?.data ?? 0,
                isLoading:
                    (queries[key]?.isLoading && queries[key]?.isFetching) ||
                    (previousQueries[previousKey]?.isLoading && previousQueries[previousKey]?.isFetching),
            }),
        [queries, previousQueries]
    );

    useEffect(() => {
        const _rows: TableRow[] = columns.map(({ label, totalKey, totalAdInfluencedKey }) => {
            const totalUniqueTraffic = buildMetric(totalKey, totalKey);
            const totalAdInfluencedTraffic = buildMetric(totalAdInfluencedKey, totalAdInfluencedKey);

            const percentAdInfluenced =
                label === 'STV/OLV/Display'
                    ? setupMetricChange({ format: 'number', value: 100, previousValue: 100, isLoading: false })
                    : setupMetricChange({
                          format: 'number',
                          value:
                              totalUniqueTraffic.value > 0
                                  ? Math.round(
                                        (totalAdInfluencedTraffic.value /
                                            (totalUniqueTraffic.value + totalAdInfluencedTraffic.value)) *
                                            100
                                    )
                                  : 0,
                          previousValue:
                              totalUniqueTraffic.previousValue &&
                              totalAdInfluencedTraffic.previousValue &&
                              totalUniqueTraffic.previousValue > 0
                                  ? Math.round(
                                        (totalAdInfluencedTraffic.previousValue /
                                            (totalUniqueTraffic.previousValue +
                                                totalAdInfluencedTraffic.previousValue)) *
                                            100
                                    )
                                  : 0,
                          isLoading: totalUniqueTraffic.isLoading || totalAdInfluencedTraffic.isLoading,
                      });

            return {
                channel: label,
                totalUniqueTraffic,
                totalAdInfluencedTraffic,
                percentAdInfluenced,
            };
        });

        // Sort the rows in descending order
        _rows.sort((a: TableRow, b: TableRow) => b.totalUniqueTraffic.value - a.totalUniqueTraffic.value);

        if (metrics?.TOTAL_UNIQUE_TRAFFIC && metrics?.TOTAL_AD_INFLUENCED_TRAFFIC) {
            const otherTotalUniqueTraffic: Metric = setupMetricChange({
                format: 'number',
                value:
                    metrics.TOTAL_UNIQUE_TRAFFIC.value -
                    _rows.reduce((sum, _row) => sum + _row.totalUniqueTraffic.value, 0),
                previousValue:
                    (metrics.TOTAL_UNIQUE_TRAFFIC.previousValue || 0) -
                    _rows.reduce((sum, _row) => sum + (_row.totalUniqueTraffic.previousValue || 0), 0),
                isLoading: false,
            });
            const otherTotalAdInfluencedTraffic: Metric = setupMetricChange({
                format: 'number',
                value:
                    metrics.TOTAL_AD_INFLUENCED_TRAFFIC.value -
                    _rows.reduce((sum, _row) => sum + _row.totalAdInfluencedTraffic.value, 0),
                previousValue:
                    (metrics.TOTAL_AD_INFLUENCED_TRAFFIC.previousValue || 0) -
                    _rows.reduce((sum, _row) => sum + (_row.totalAdInfluencedTraffic.previousValue || 0), 0),
                isLoading: false,
            });
            const otherPercentAdInfluenced: Metric = setupMetricChange({
                format: 'number',
                value:
                    otherTotalUniqueTraffic.value > 0
                        ? Math.round(
                              (otherTotalAdInfluencedTraffic.value /
                                  (otherTotalUniqueTraffic.value + otherTotalAdInfluencedTraffic.value)) *
                                  100
                          )
                        : 0,
                previousValue:
                    otherTotalUniqueTraffic.previousValue &&
                    otherTotalAdInfluencedTraffic.previousValue &&
                    otherTotalUniqueTraffic.previousValue > 0
                        ? Math.round(
                              (otherTotalAdInfluencedTraffic.previousValue /
                                  (otherTotalUniqueTraffic.previousValue +
                                      otherTotalAdInfluencedTraffic.previousValue)) *
                                  100
                          )
                        : 0,
                isLoading: otherTotalUniqueTraffic.isLoading || otherTotalAdInfluencedTraffic.isLoading,
            });

            _rows.push({
                channel: 'Other',
                totalUniqueTraffic: otherTotalUniqueTraffic,
                totalAdInfluencedTraffic: otherTotalAdInfluencedTraffic,
                percentAdInfluenced: otherPercentAdInfluenced,
                tooltip:
                    'It represents traffic sources not included in the primary list, such as email or SMS campaigns.',
            });

            _rows.push({
                channel: 'Total',
                totalUniqueTraffic: metrics.TOTAL_UNIQUE_TRAFFIC,
                totalAdInfluencedTraffic: metrics.TOTAL_AD_INFLUENCED_TRAFFIC,
                percentAdInfluenced: setupMetricChange({
                    format: 'number',
                    value:
                        metrics.TOTAL_UNIQUE_TRAFFIC.value > 0
                            ? Math.round(
                                  (metrics.TOTAL_AD_INFLUENCED_TRAFFIC.value /
                                      (metrics.TOTAL_UNIQUE_TRAFFIC.value +
                                          metrics.TOTAL_AD_INFLUENCED_TRAFFIC.value)) *
                                      100
                              )
                            : 0,
                    previousValue:
                        metrics.TOTAL_UNIQUE_TRAFFIC.previousValue &&
                        metrics.TOTAL_AD_INFLUENCED_TRAFFIC.previousValue &&
                        metrics.TOTAL_UNIQUE_TRAFFIC.previousValue > 0
                            ? Math.round(
                                  (metrics.TOTAL_AD_INFLUENCED_TRAFFIC.previousValue /
                                      (metrics.TOTAL_UNIQUE_TRAFFIC.previousValue +
                                          metrics.TOTAL_AD_INFLUENCED_TRAFFIC.previousValue)) *
                                      100
                              )
                            : 0,
                    isLoading:
                        metrics.TOTAL_UNIQUE_TRAFFIC?.isLoading || metrics.TOTAL_AD_INFLUENCED_TRAFFIC?.isLoading,
                }),
                tooltip: '',
            });
        }

        setRows(_rows);
    }, [queries, previousQueries, metrics, buildMetric]);

    return { rows, setRows };
};

const HatChannelTrafficTable: FC<TableProps> = ({ rows }): React.JSX.Element => {
    const { hasHatCriteriaPreviousDateRange } = useHatDashboardPageContext();
    return (
        <TableContainer
            sx={{
                borderCollapse: 'inherit',
                borderColor: (theme) => theme.palette.grey[300],
                borderStyle: 'solid',
                borderWidth: 1,
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <Table
                sx={{
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(1)`]: {
                        textAlign: 'left',
                        width: 'auto',
                    },
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(4)`]: {
                        width: '12vw',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Channel</TableCell>
                        <TableCell>Total Visits</TableCell>
                        <TableCell>
                            <TableCellText tooltip="Visits directly attributed to our campaigns and reclassified from other channels.">
                                Attributed Visits
                            </TableCellText>
                        </TableCell>
                        <TableCell>% Attributed Visits</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(
                        (
                            { channel, totalUniqueTraffic, totalAdInfluencedTraffic, percentAdInfluenced, tooltip },
                            index
                        ) => (
                            <TableRow key={index}>
                                <TablePrimaryCell>
                                    {tooltip ? (
                                        <TableCellText tooltip={tooltip} justifyContent="flex-start">
                                            {channel}
                                        </TableCellText>
                                    ) : (
                                        channel
                                    )}
                                </TablePrimaryCell>
                                {[totalUniqueTraffic, totalAdInfluencedTraffic, percentAdInfluenced].map(
                                    (metric, i) => (
                                        <TableCell key={i}>
                                            <Column gap={0} alignItems="flex-end">
                                                {Utils.formatValue(metric.value, i === 2 ? 'percent' : undefined)}

                                                {hasHatCriteriaPreviousDateRange && (
                                                    <MetricChangeLabel
                                                        variant="body2"
                                                        label={HatUtils.formatPercent(metric.change)}
                                                        direction={
                                                            metric.change && metric.change !== 0
                                                                ? metric.change > 0
                                                                    ? 'up'
                                                                    : 'down'
                                                                : 'center'
                                                        }
                                                    />
                                                )}
                                            </Column>
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        )
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export type { TableState as UseHatChannelTrafficTableState, TableProps as HatChannelTrafficTableProps };
export { useHatChannelTrafficTable };
export default HatChannelTrafficTable;
